import { useMemo } from "react";
import { useConnections, useRelationships, useUpdateThread } from "@/api/queries";
import { Menu } from "@/components/ui/Menu";
import { CloseIcon, DatabaseIcon, LinkIcon, PlusIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * Which sources this conversation asks (PRD FR-4.11, MS-4).
 *
 * A chip per source, primary first, each removable while another remains; an
 * *Add source* menu with the sources this conversation's sources have declared
 * relationships with listed first; and, when one of those related sources is
 * not in the conversation yet, a quiet chip offering it.
 *
 * Changing the list affects the **next** answer only. Answers already given
 * keep the sources their run could see, and say so on their own chips — so
 * nothing here rewrites what an earlier answer used.
 *
 * Adding or removing is a mutation with the toast default (backlog item 11):
 * the server's sentence — "at most 4 sources" — is the message.
 */
export function SourceBar({
  threadId,
  connectionIds,
  disabled = false,
}: {
  threadId: string;
  connectionIds: string[];
  disabled?: boolean;
}) {
  const { data: connections } = useConnections();
  const { data: relationships } = useRelationships();
  const update = useUpdateThread(threadId);

  const names = useMemo(
    () => Object.fromEntries((connections ?? []).map((row) => [row.id, row.name])),
    [connections],
  );

  // Sources related to any source this conversation already has.
  const related = useMemo(() => {
    const out = new Set<string>();
    for (const row of relationships ?? []) {
      if (row.stale) continue;
      const left = row.left.connection_id;
      const right = row.right.connection_id;
      if (connectionIds.includes(left) && !connectionIds.includes(right)) out.add(right);
      if (connectionIds.includes(right) && !connectionIds.includes(left)) out.add(left);
    }
    return out;
  }, [relationships, connectionIds]);

  const others = useMemo(
    () =>
      (connections ?? [])
        .filter((row) => !connectionIds.includes(row.id))
        .sort((a, b) => Number(related.has(b.id)) - Number(related.has(a.id))),
    [connections, connectionIds, related],
  );
  const suggestion = others.find((row) => related.has(row.id));

  const set = (ids: string[]) => update.mutate({ connection_ids: ids });

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5" aria-label="Sources in this conversation">
      {connectionIds.map((id, position) => (
        <span
          key={id}
          className={cn(
            "type-caption inline-flex items-center gap-1 rounded-full border py-0.5 ps-2",
            connectionIds.length > 1 ? "pe-0.5" : "pe-2",
            position === 0 ? "border-border-emphasized text-ink" : "border-border text-ink-secondary",
          )}
          title={position === 0 ? "Primary source — its preferred model answers" : undefined}
        >
          <DatabaseIcon className="size-3" />
          <span className="normal-case">{names[id] ?? "Removed source"}</span>
          {connectionIds.length > 1 ? (
            <button
              type="button"
              disabled={disabled || update.isPending}
              onClick={() => set(connectionIds.filter((other) => other !== id))}
              aria-label={`Stop using ${names[id] ?? "this source"} in this conversation`}
              className="text-ink-disabled hover:text-ink hover:bg-hover rounded-full p-0.5 motion-fast disabled:opacity-50"
            >
              <CloseIcon className="size-3" />
            </button>
          ) : null}
        </span>
      ))}

      {others.length ? (
        <Menu
          label="Add a source"
          align="start"
          className="min-w-56"
          trigger={(props) => (
            <button
              type="button"
              disabled={disabled || update.isPending}
              className="type-caption text-ink-secondary hover:text-ink hover:bg-hover rounded-full inline-flex items-center gap-1 px-2 py-0.5 motion-fast disabled:opacity-50"
              {...props}
            >
              <PlusIcon className="size-3" />
              Add source
            </button>
          )}
        >
          {(close) =>
            others.map((row) => (
              <button
                key={row.id}
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  set([...connectionIds, row.id]);
                }}
                className="type-support text-ink hover:bg-hover rounded-inner motion-fast flex items-center gap-2 px-2 py-1.5 text-start"
              >
                <DatabaseIcon className="text-ink-disabled size-3.5" />
                <span className="flex-1">{row.name}</span>
                {related.has(row.id) ? (
                  <span className="type-caption text-accent inline-flex items-center gap-1">
                    <LinkIcon className="size-3" />
                    related
                  </span>
                ) : null}
              </button>
            ))
          }
        </Menu>
      ) : null}

      {suggestion ? (
        <button
          type="button"
          disabled={disabled || update.isPending}
          onClick={() => set([...connectionIds, suggestion.id])}
          className="type-caption text-accent hover:bg-accent-subtle rounded-full inline-flex items-center gap-1 px-2 py-0.5 normal-case motion-fast disabled:opacity-50"
        >
          <LinkIcon className="size-3" />
          {suggestion.name} is related — add it?
        </button>
      ) : null}
    </div>
  );
}
